/**
 * Event Management - Manage events for a specific calendar
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import apiService from '../../services/api';
import { AdminPageLayout, ConfirmDialog } from '../../components';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  EditAction,
  DeleteAction,
  CreateAction
} from '../../components/listing/v2/ListingAction';
import { Activity, Trophy, Users, Medal, PartyPopper, Banknote, Calendar, X, AlertTriangle, Eye, Clock, MapPin, Plus, Edit2, Trash2 } from 'lucide-react';
import { formatEventDateTime } from '../../utils/datetime';
import { useToast } from '../../contexts/ToastContext';

const EventManagement = () => {
  const { calendarId } = useParams();
  console.log('[EventManagement] Calendar ID from URL:', calendarId);
  console.log('[EventManagement] Calendar ID length:', calendarId?.length);
  console.log('[EventManagement] Calendar ID type:', typeof calendarId);
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [calendar, setCalendar] = useState(null);
  const [events, setEvents] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    viewFilter: 'upcoming'
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);

  const eventTypes = [
    { value: 'practice', label: 'Practice', icon: <Activity size={16} /> },
    { value: 'game', label: 'Game', icon: <Trophy size={16} /> },
    { value: 'scrimmage', label: 'Scrimmage', icon: <Users size={16} /> },
    { value: 'tournament', label: 'Tournament', icon: <Medal size={16} /> },
    { value: 'meeting', label: 'Meeting', icon: <Users size={16} /> },
    { value: 'social_event', label: 'Social Event', icon: <PartyPopper size={16} /> },
    { value: 'fundraiser', label: 'Fundraiser', icon: <Banknote size={16} /> },
    { value: 'custom', label: 'Custom', icon: <Calendar size={16} /> }
  ];

  // Helper function to convert local datetime string to UTC ISO string
  const toUTCString = (localDateTimeString) => {
    const date = new Date(localDateTimeString);
    return date.toISOString();
  };

  // Helper function to convert UTC ISO string to local input datetime format
  const toLocalInputString = (utcISOString) => {
    const date = new Date(utcISOString);
    // Format as: YYYY-MM-DDTHH:mm for datetime-local input
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Removed eventScopes; event scope is set by calendar

  useEffect(() => {
    loadCalendarAndEvents();
  }, [calendarId, filters.viewFilter]);

  useEffect(() => {
    loadRoles();
  }, []);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const loadCalendarAndEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[EventManagement] Loading events with filter:', filters.viewFilter);

      // Load calendar details and events in parallel
      const [calendarResponse, eventsResponse] = await Promise.all([
        apiService.request(`/api/calendars/${calendarId}`),
        apiService.getCalendarEvents(calendarId, {
          time_filter: filters.viewFilter,
          page_size: 100
        })
      ]);

      console.log('[EventManagement] Calendar:', calendarResponse);
      console.log('[EventManagement] Events response:', eventsResponse);
      console.log('[EventManagement] Events count:', eventsResponse.events?.length || 0);

      setCalendar(calendarResponse);
      const loadedEvents = eventsResponse.events || [];
      setEvents(loadedEvents);

      console.log('[EventManagement] Set events:', loadedEvents.length);

    } catch (err) {
      console.error('Error loading calendar and events:', err);
      setError('Failed to load calendar and events');
      showError('Failed to load calendar and events');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await apiService.getClubRoles(true);
      setRoles(response.roles || []);
    } catch (err) {
      console.error('Error loading roles:', err);
      // Don't set error state - roles are optional for display
    }
  };

  const getVisibilityBadges = (visibilityRoles) => {
    if (!visibilityRoles || visibilityRoles.length === 0) {
      return [{ label: 'All Members', type: 'all' }];
    }

    const badges = [];

    visibilityRoles.forEach(roleValue => {
      if (roleValue === 'all') {
        badges.push({ label: 'All Members', type: 'all' });
      } else if (roleValue === 'staff') {
        badges.push({ label: 'Staff Only', type: 'staff' });
      } else {
        // Find the role by ID
        const role = roles.find(r => r.id === roleValue);
        if (role) {
          badges.push({
            label: role.name,
            type: role.is_staff ? 'staff-role' : 'member-role'
          });
        }
      }
    });

    return badges.length > 0 ? badges : [{ label: 'All Members', type: 'all' }];
  };

  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;

    try {
      setLoading(true);
      await apiService.deleteCalendarEvent(eventToDelete.id);
      showSuccess(`Event "${eventToDelete.title}" deleted successfully`);
      await loadCalendarAndEvents();
      setShowDeleteConfirm(false);
      setEventToDelete(null);
    } catch (err) {
      console.error('Error deleting event:', err);
      showError('Failed to delete event');
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (eventType) => {
    const eventTypeObj = eventTypes.find(t => t.value === eventType);
    return eventTypeObj ? eventTypeObj.icon : <Calendar size={32} />;
  };

  const getEventTypeLabel = (eventType) => {
    const eventTypeObj = eventTypes.find(t => t.value === eventType);
    return eventTypeObj ? eventTypeObj.label : eventType;
  };

  const filteredEvents = events.filter(event => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      if (!event.title.toLowerCase().includes(searchLower) &&
        !(event.description?.toLowerCase().includes(searchLower)) &&
        !(event.location_name?.toLowerCase().includes(searchLower))) {
        return false;
      }
    }
    return true;
  });

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      filters={[
        {
          name: 'viewFilter',
          label: 'View',
          options: [
            { value: 'upcoming', label: 'Upcoming Events' },
            { value: 'all', label: 'All Events' },
            { value: 'past', label: 'Past Events' }
          ]
        }
      ]}
      activeFilters={{ viewFilter: filters.viewFilter }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={() => setFilters({ search: '', viewFilter: 'upcoming' })}
    />
  );

  const headerActions = (
    <ListingActionBar
      rightSection={
        <CreateAction
          onClick={() => navigate(`/admin/calendars/${calendarId}/events/add`)}
          label="New Event"
        />
      }
    />
  );

  return (
    <AdminPageLayout
      title="EVENT MANAGEMENT"
      subtitle={calendar ? `Manage events for ${calendar.name}` : 'Manage events'}
      backPath="/admin/calendars"
      maxWidth="1400px"
    >
      <Listing
        loading={loading}
        items={filteredEvents}
        actions={headerActions}
        filters={filterBar}
        renderItem={(event) => {
          const { date, time } = formatEventDateTime(event.start_time);
          const eventTypeLabel = getEventTypeLabel(event.event_type);
          
          // Get event color (prioritize event color, fallback to calendar color, then default)
          const eventColor = event.color_code || event.calendar_color || '#2196F3';
          
          // Color indicator badge
          const colorBadge = (
            <div
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: eventColor,
                border: '2px solid var(--color-background)',
                boxShadow: '0 0 0 1px var(--color-border)',
                flexShrink: 0
              }}
              title={`Event color: ${eventColor}`}
            />
          );

          return (
            <ListingItem
              key={event.id}
              title={event.title}
              subtitle={eventTypeLabel}
              icon={getEventIcon(event.event_type)}
              iconVariant="primary"
              badges={[colorBadge]}
              actions={
                <div style={{ display: 'flex', gap: '8px' }}>
                  <EditAction
                    size="xs"
                    onClick={() => navigate(`/admin/calendars/${calendarId}/events/${event.id}/edit`)}
                  />
                  <DeleteAction
                    size="xs"
                    onClick={() => {
                      setEventToDelete({ id: event.id, title: event.title });
                      setShowDeleteConfirm(true);
                    }}
                  />
                </div>
              }
            >
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem', fontSize: '0.875rem', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ color: '#999', marginBottom: '0.25rem' }}>Date</div>
                  <div style={{ fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Calendar size={14} /> {date}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#999', marginBottom: '0.25rem' }}>Time</div>
                  <div style={{ fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Clock size={14} /> {time}
                  </div>
                </div>
                {event.location_name && (
                  <div>
                    <div style={{ color: '#999', marginBottom: '0.25rem' }}>Location</div>
                    <div style={{ fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <MapPin size={14} /> {event.location_name}
                    </div>
                  </div>
                )}
                {event.max_participants && (
                  <div>
                    <div style={{ color: '#999', marginBottom: '0.25rem' }}>Max Participants</div>
                    <div style={{ fontWeight: '500' }}>{event.max_participants}</div>
                  </div>
                )}
              </div>
              {event.description && (
                <div style={{ marginTop: '0.75rem', color: '#666', fontSize: '0.875rem' }}>
                  {event.description}
                </div>
              )}
              {event.visibility_roles && event.visibility_roles.length > 0 && (
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <Eye size={14} style={{ color: '#999' }} />
                  {getVisibilityBadges(event.visibility_roles).map((badge, idx) => (
                    <span
                      key={idx}
                      style={{
                        fontSize: '0.75rem',
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#f0f0f0',
                        borderRadius: '4px',
                        color: '#666'
                      }}
                    >
                      {badge.label}
                    </span>
                  ))}
                </div>
              )}
            </ListingItem>
          );
        }}
        emptyMessage={
          filters.viewFilter === 'upcoming'
            ? 'No upcoming events scheduled'
            : `No ${filters.viewFilter} events found`
        }
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setEventToDelete(null);
        }}
        onConfirm={handleDeleteEvent}
        title="Delete Event"
        message={`Are you sure you want to delete "${eventToDelete?.title}"?`}
        details="This action cannot be undone."
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
        loading={loading}
      />
    </AdminPageLayout>
  );
};

export default EventManagement;